<template>
  <section class="page-stack">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Gear Insights</p>
          <h2>Best gear by specialization</h2>
          <p>Aggregated from top Mythic+ leaderboard characters and cached locally for fast return visits.</p>
        </div>
      </header>
    </article>

    <div class="card filter-toolbar gear-insights-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Season: <span>{{ analysis?.seasonLabel || 'Current' }}</span></span>
        <span class="filter-pill">Region: <span>{{ analysis?.region || filters.region }}</span></span>
        <span class="filter-pill">Players: <span>{{ analyzedCharactersCount }}</span></span>
        <span class="filter-pill">Cache: <span>{{ cacheStatusLabel }}</span></span>
      </div>
      <div class="page-actions">
        <button class="btn-icon-outline" type="button" aria-label="Refresh analysis" title="Refresh analysis" :disabled="loading" @click="refreshAnalysis">
          <i class="fa-solid fa-rotate" :class="{ 'is-spinning': loading }"></i>
        </button>
      </div>
    </div>

    <div v-if="error" class="card">
      <section>{{ error }}</section>
    </div>

    <section v-if="loading" class="gear-insights-progress card">
      <header class="section-head">
        <div>
          <p class="eyebrow">Analyzing</p>
          <h3>{{ loadingMessage }}</h3>
        </div>
        <strong>{{ progressLabel }}</strong>
      </header>
      <div class="gear-progress-track">
        <span :style="{ width: progressPercent }"></span>
      </div>
    </section>

    <section v-if="classOptions.length" class="gear-selector-stack">
      <article class="card gear-choice-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Class</p>
            <h3>{{ selectedClassLabel }}</h3>
          </div>
        </header>
        <div class="gear-class-switch">
          <button
            v-for="classItem in classOptions"
            :key="classItem.key"
            type="button"
            :class="{ 'is-active': selectedClassKey === classItem.key }"
            :style="{ '--class-color': classItem.color }"
            @click="selectClass(classItem.key)"
          >
            <img v-if="classItem.icon" :src="classItem.icon" :alt="classItem.name" loading="lazy">
            <span>{{ classItem.name }}</span>
          </button>
        </div>
      </article>

      <article v-if="specOptions.length" class="card gear-choice-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Specialization</p>
            <h3>{{ selectedSpecLabel }}</h3>
          </div>
        </header>
        <div class="gear-spec-switch view-switch">
          <button
            v-for="spec in specOptions"
            :key="spec.key"
            class="btn-sm-outline"
            type="button"
            :class="{ 'is-active': selectedSpecKey === spec.key }"
            @click="selectedSpecKey = spec.key"
          >
            {{ spec.name }}
          </button>
        </div>
      </article>
    </section>

    <article v-if="selectedSpecData" class="gear-insights-summary">
      <section class="card gear-sample-panel">
        <header class="section-head">
          <div>
            <p class="eyebrow">Best set</p>
            <h3>{{ selectedSpecData.specName }} {{ selectedSpecData.className }}</h3>
          </div>
          <span class="filter-pill">Sample: <span>{{ selectedSpecData.characterCount }}</span></span>
        </header>

        <section class="armory-frame gear-insights-armory">
          <div class="armory-column armory-left">
            <a
              v-for="slot in leftGearSlots"
              :key="slot"
              class="armory-item"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <img v-if="bestItem(slot)" :src="itemIconUrl(bestItem(slot))" :alt="bestItem(slot).name" loading="lazy">
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
            </a>
          </div>

          <div class="armory-column armory-right">
            <a
              v-for="slot in rightGearSlots"
              :key="slot"
              class="armory-item is-right"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
              <img v-if="bestItem(slot)" :src="itemIconUrl(bestItem(slot))" :alt="bestItem(slot).name" loading="lazy">
            </a>
          </div>

          <div class="armory-weapons">
            <a
              v-for="slot in weaponGearSlots"
              :key="slot"
              class="armory-item armory-weapon"
              :class="{ 'is-empty': !bestItem(slot) }"
              :href="wowheadItemUrl(bestItem(slot))"
              :data-wowhead="wowheadItemData(bestItem(slot))"
              target="_blank"
              rel="noreferrer"
              :title="slotLabel(slot)"
              @click.prevent="openSlot(slot)"
            >
              <img v-if="bestItem(slot)" :src="itemIconUrl(bestItem(slot))" :alt="bestItem(slot).name" loading="lazy">
              <span>
                <strong>{{ itemName(slot) }}</strong>
                <small>{{ itemMeta(slot) }}</small>
              </span>
            </a>
          </div>
        </section>
      </section>
    </article>

    <AppEmptyState
      v-else-if="!loading"
      icon="fa-solid fa-shirt"
      title="No gear analysis yet"
      message="Refresh the page data to analyze leaderboard characters and build specialization gear picks."
      :actions="[]"
    />

    <div
      v-if="activeSlot"
      class="app-modal-backdrop gear-backdrop"
      :class="{ 'is-closing': closingSlot }"
      @click.self="closeSlot"
    >
      <section class="app-modal gear-top-modal" role="dialog" aria-modal="true" aria-labelledby="gear-top-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">Top 5 items</p>
            <h2 id="gear-top-title">{{ slotLabel(activeSlot) }}</h2>
          </div>
          <button class="btn-icon-outline" type="button" aria-label="Close item list" @click="closeSlot">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>
        <section class="gear-top-list">
          <a
            v-for="(item, index) in activeSlotItems"
            :key="`${activeSlot}-${item.itemId}`"
            class="gear-top-item"
            :href="wowheadItemUrl(item)"
            :data-wowhead="wowheadItemData(item)"
            target="_blank"
            rel="noreferrer"
          >
            <span class="gear-top-rank">#{{ index + 1 }}</span>
            <img v-if="itemIconUrl(item)" :src="itemIconUrl(item)" :alt="item.name" loading="lazy">
            <span>
              <strong>{{ item.name }}</strong>
              <small>{{ item.count }} equipped · {{ item.share }}%</small>
            </span>
            <i class="fa-solid fa-arrow-up-right-from-square"></i>
          </a>
        </section>
      </section>
    </div>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import { getCharacterProfile, getMythicPlusRuns, getMythicPlusStaticData } from '../services/raiderApi'
import deathKnightIcon from '../assets/images/classes/death knight.png'
import demonHunterIcon from '../assets/images/classes/demon hunter.png'
import druidIcon from '../assets/images/classes/druid.png'
import evokerIcon from '../assets/images/classes/evoker.png'
import hunterIcon from '../assets/images/classes/hunter.png'
import mageIcon from '../assets/images/classes/mage.png'
import monkIcon from '../assets/images/classes/monk.png'
import paladinIcon from '../assets/images/classes/paladin.png'
import priestIcon from '../assets/images/classes/priest.png'
import rogueIcon from '../assets/images/classes/rogue.png'
import shamanIcon from '../assets/images/classes/shaman.png'
import warlockIcon from '../assets/images/classes/warlock.png'
import warriorIcon from '../assets/images/classes/warrior.png'

const CACHE_KEY = 'rio-dashboard-gear-insights'
const CACHE_VERSION = 4
const CACHE_TTL = 24 * 60 * 60 * 1000
const ICON_BASE_URL = 'https://render.worldofwarcraft.com/us/icons/56'
const LEADERBOARD_PAGE_COUNT = 10
const PROFILE_BATCH_SIZE = 5
const TOP_ITEM_LIMIT = 5

const CLASS_ICONS = {
  deathknight: deathKnightIcon,
  'death-knight': deathKnightIcon,
  'death knight': deathKnightIcon,
  demonhunter: demonHunterIcon,
  'demon-hunter': demonHunterIcon,
  'demon hunter': demonHunterIcon,
  druid: druidIcon,
  evoker: evokerIcon,
  hunter: hunterIcon,
  mage: mageIcon,
  monk: monkIcon,
  paladin: paladinIcon,
  priest: priestIcon,
  rogue: rogueIcon,
  shaman: shamanIcon,
  warlock: warlockIcon,
  warrior: warriorIcon,
}

const CLASS_COLORS = {
  deathknight: '#c41e3a',
  'death-knight': '#c41e3a',
  'death knight': '#c41e3a',
  demonhunter: '#a330c9',
  'demon-hunter': '#a330c9',
  'demon hunter': '#a330c9',
  druid: '#ff7c0a',
  evoker: '#33937f',
  hunter: '#aad372',
  mage: '#3fc7eb',
  monk: '#00ff98',
  paladin: '#f48cba',
  priest: '#ffffff',
  rogue: '#fff468',
  shaman: '#0070dd',
  warlock: '#8788ee',
  warrior: '#c69b6d',
}

const SLOT_LABELS = {
  head: 'Head',
  neck: 'Neck',
  shoulder: 'Shoulder',
  back: 'Back',
  chest: 'Chest',
  wrist: 'Wrist',
  hands: 'Hands',
  waist: 'Waist',
  legs: 'Legs',
  feet: 'Feet',
  finger1: 'Finger',
  finger2: 'Finger',
  finger: 'Finger',
  trinket1: 'Trinket',
  trinket2: 'Trinket',
  trinket: 'Trinket',
  mainhand: 'Main hand',
  offhand: 'Off hand',
}

function normalizeKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
}

function normalizeSlotKey(slot) {
  return String(slot || '')
    .toLowerCase()
    .replace(/_/g, '')
    .replace(/-/g, '')
    .replace(/\s/g, '')
}

function loadCachedAnalysis() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')
    return cached?.cacheVersion === CACHE_VERSION ? cached : null
  } catch (error) {
    return null
  }
}

function saveCachedAnalysis(payload) {
  localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
}

const analysisJobState = {
  loading: false,
  loadingMessage: 'Preparing leaderboard data',
  progressCurrent: 0,
  progressTotal: 0,
  error: '',
  result: null,
  promise: null,
}

const analysisJobListeners = new Set()

function analysisJobSnapshot() {
  return { ...analysisJobState }
}

function notifyAnalysisJob() {
  const snapshot = analysisJobSnapshot()
  analysisJobListeners.forEach((listener) => listener(snapshot))
}

function setAnalysisJobState(patch) {
  Object.assign(analysisJobState, patch)
  notifyAnalysisJob()
}

function subscribeAnalysisJob(listener) {
  analysisJobListeners.add(listener)
  listener(analysisJobSnapshot())
  return () => analysisJobListeners.delete(listener)
}

export default {
  name: 'GearInsightsView',
  components: { AppEmptyState },
  data() {
    return {
      loading: false,
      loadingMessage: 'Preparing leaderboard data',
      progressCurrent: 0,
      progressTotal: 0,
      error: '',
      analysis: null,
      selectedClassKey: '',
      selectedSpecKey: '',
      activeSlot: null,
      closingSlot: false,
      unsubscribeAnalysisJob: null,
      filters: {
        expansionId: 11,
        region: 'world',
        dungeon: 'all',
        season: '',
      },
      leftGearSlots: ['head', 'neck', 'shoulder', 'back', 'chest', 'shirt', 'tabard', 'wrist'],
      rightGearSlots: ['hands', 'waist', 'legs', 'feet', 'finger1', 'finger2', 'trinket1', 'trinket2'],
      weaponGearSlots: ['mainhand', 'offhand'],
    }
  },
  computed: {
    cacheStatusLabel() {
      if (!this.analysis?.createdAt) return 'Empty'
      const age = Date.now() - Number(this.analysis.createdAt)
      if (age < 60 * 1000) return 'Just now'
      if (age < 60 * 60 * 1000) return `${Math.round(age / 60000)} min ago`
      return `${Math.round(age / 3600000)} h ago`
    },
    analyzedCharactersCount() {
      return this.analysis?.characterCount || 0
    },
    classOptions() {
      const classes = this.analysis?.classes || {}
      return Object.entries(classes)
        .map(([key, item]) => ({
          key,
          name: item.name,
          color: CLASS_COLORS[key] || 'var(--primary)',
          icon: CLASS_ICONS[key],
          count: item.characterCount || 0,
        }))
        .sort((left, right) => left.name.localeCompare(right.name))
    },
    specOptions() {
      const specs = this.analysis?.classes?.[this.selectedClassKey]?.specs || {}
      return Object.entries(specs)
        .map(([key, item]) => ({
          key,
          name: item.name || item.specName || 'Unknown',
          count: item.characterCount || 0,
        }))
        .sort((left, right) => left.name.localeCompare(right.name))
    },
    selectedClassLabel() {
      return this.classOptions.find((item) => item.key === this.selectedClassKey)?.name || 'Choose class'
    },
    selectedSpecLabel() {
      return this.specOptions.find((item) => item.key === this.selectedSpecKey)?.name || 'Choose spec'
    },
    selectedSpecData() {
      return this.analysis?.classes?.[this.selectedClassKey]?.specs?.[this.selectedSpecKey] || null
    },
    displaySlotOrder() {
      return [...this.leftGearSlots, ...this.rightGearSlots, ...this.weaponGearSlots]
    },
    selectedBestItems() {
      if (!this.selectedSpecData?.slots) return {}

      return this.displaySlotOrder.reduce((items, slot) => {
        items[slot] = this.itemForDisplaySlot(slot)
        return items
      }, {})
    },
    activeSlotItems() {
      return this.itemsForSlot(this.activeSlot).slice(0, TOP_ITEM_LIMIT)
    },
    progressLabel() {
      if (!this.progressTotal) return ''
      return `${this.progressCurrent}/${this.progressTotal}`
    },
    progressPercent() {
      if (!this.progressTotal) return '8%'
      return `${Math.max(8, Math.round((this.progressCurrent / this.progressTotal) * 100))}%`
    },
  },
  mounted() {
    this.unsubscribeAnalysisJob = subscribeAnalysisJob(this.syncAnalysisJob)
    this.loadAnalysis()
    window.addEventListener('keydown', this.closeSlotOnEscape)
  },
  beforeUnmount() {
    if (this.unsubscribeAnalysisJob) {
      this.unsubscribeAnalysisJob()
      this.unsubscribeAnalysisJob = null
    }
    window.removeEventListener('keydown', this.closeSlotOnEscape)
    document.body.classList.remove('is-scroll-locked')
  },
  methods: {
    syncAnalysisJob(snapshot) {
      this.loading = snapshot.loading
      this.loadingMessage = snapshot.loadingMessage
      this.progressCurrent = snapshot.progressCurrent
      this.progressTotal = snapshot.progressTotal
      this.error = snapshot.error

      if (snapshot.result && snapshot.result.createdAt !== this.analysis?.createdAt) {
        this.applyAnalysis(snapshot.result)
      }
    },
    async loadAnalysis() {
      if (analysisJobState.loading) {
        return
      }

      const cached = loadCachedAnalysis()
      if (cached?.analysis && Date.now() - Number(cached.createdAt || 0) < CACHE_TTL) {
        this.applyAnalysis(cached)
        return
      }

      await this.buildAnalysis()
    },
    async refreshAnalysis() {
      await this.buildAnalysis(true)
    },
    applyAnalysis(payload) {
      this.analysis = payload
      this.selectedClassKey = this.classOptions[0]?.key || ''
      this.selectedSpecKey = this.specOptions[0]?.key || ''
      this.$nextTick(() => this.refreshWowheadTooltips())
    },
    selectClass(classKey) {
      this.selectedClassKey = classKey
      this.selectedSpecKey = this.specOptions[0]?.key || ''
    },
    async buildAnalysis(force = false) {
      if (analysisJobState.promise) {
        return analysisJobState.promise
      }

      setAnalysisJobState({
        loading: true,
        loadingMessage: 'Loading Mythic+ leaderboard',
        progressCurrent: 0,
        progressTotal: 0,
        error: '',
        result: null,
      })
      this.$store.dispatch('startGlobalLoading', 'Analyzing leaderboard gear...')

      const promise = (async () => {
        const staticData = await getMythicPlusStaticData(this.filters.expansionId)
        const season = this.pickDefaultSeason(staticData.seasons || [])
        const seasonLabel = season?.short_name || season?.name || 'Current'
        const runFilters = {
          season: season?.slug || '',
          region: this.filters.region,
          dungeon: this.filters.dungeon,
        }
        const pages = Array.from({ length: LEADERBOARD_PAGE_COUNT }, (_, page) => page)
        const runPages = await Promise.all(pages.map((page) => getMythicPlusRuns({ ...runFilters, page }).catch(() => ({ rankings: [] }))))
        const characters = this.uniqueCharacters(runPages.flatMap((page) => page.rankings || []))

        setAnalysisJobState({
          loadingMessage: 'Fetching character gear',
          progressCurrent: 0,
          progressTotal: characters.length,
        })

        const profiles = []
        for (let index = 0; index < characters.length; index += PROFILE_BATCH_SIZE) {
          const batch = characters.slice(index, index + PROFILE_BATCH_SIZE)
          const results = await Promise.allSettled(batch.map((character) => this.fetchProfile(character)))
          profiles.push(...results.filter((result) => result.status === 'fulfilled').map((result) => result.value))
          setAnalysisJobState({
            progressCurrent: Math.min(characters.length, index + batch.length),
          })
        }

        setAnalysisJobState({
          loadingMessage: 'Aggregating best items',
        })
        const payload = {
          cacheVersion: CACHE_VERSION,
          createdAt: Date.now(),
          season: season?.slug || '',
          seasonLabel,
          region: this.filters.region,
          dungeon: this.filters.dungeon,
          characterCount: profiles.length,
          leaderboardCharacterCount: characters.length,
          analysis: true,
          classes: this.aggregateProfiles(profiles),
        }

        saveCachedAnalysis(payload)
        setAnalysisJobState({
          result: payload,
        })
        this.$store.dispatch('setSeasonLabel', seasonLabel)
        this.$store.dispatch('notify', {
          type: 'success',
          title: force ? 'Gear refreshed' : 'Gear analysis ready',
          description: `Analyzed ${profiles.length} leaderboard characters.`,
        })
        return payload
      })()

      analysisJobState.promise = promise

      try {
        return await promise
      } catch (error) {
        const message = 'Unable to build gear analysis. Try refreshing again later.'
        setAnalysisJobState({
          error: message,
        })
        this.$store.dispatch('notify', {
          type: 'danger',
          title: 'Gear analysis failed',
          description: message,
        })
      } finally {
        setAnalysisJobState({
          loading: false,
          promise: null,
        })
        this.$store.dispatch('finishGlobalLoading')
      }
    },
    pickDefaultSeason(seasons) {
      const seasonOptions = seasons
        .filter((season) => season.is_main_season)
        .sort((left, right) => new Date(right.starts?.us || 0) - new Date(left.starts?.us || 0))
      const now = Date.now()
      return seasonOptions.find((season) => {
        const start = new Date(season.starts?.us || season.starts?.eu || 0).getTime()
        const end = new Date(season.ends?.us || season.ends?.eu || 0).getTime()
        return start <= now && now <= end
      }) || seasonOptions[0] || null
    },
    uniqueCharacters(rankings) {
      const map = new Map()
      rankings.forEach((entry) => {
        ;(entry.run?.roster || []).forEach((member) => {
          const character = member.character || {}
          const key = character.path || `${character.region?.slug}-${character.realm?.slug}-${character.name}`.toLowerCase()
          if (!character.name || map.has(key)) return
          map.set(key, {
            ...character,
            fallbackClassName: character.class?.name || character.class?.slug || '',
            fallbackSpecName: character.spec?.name || character.spec?.slug || '',
          })
        })
      })
      return Array.from(map.values())
    },
    async fetchProfile(character) {
      const profile = await getCharacterProfile({
        region: character.region?.slug,
        realm: character.realm?.slug,
        name: character.name,
        fields: 'gear,mythic_plus_scores_by_season:current,mythic_plus_ranks',
      })
      return {
        ...profile,
        fallbackClassName: character.fallbackClassName,
        fallbackSpecName: character.fallbackSpecName,
      }
    },
    aggregateProfiles(profiles) {
      const classes = {}

      profiles.forEach((profile) => {
        const className = profile.class || profile.class_name || profile.fallbackClassName || 'Unknown'
        const specName = profile.active_spec_name || profile.spec || profile.fallbackSpecName || 'Unknown'
        if (className === 'Unknown' || specName === 'Unknown') return
        const classKey = normalizeKey(className)
        const specKey = normalizeKey(specName)

        if (!classes[classKey]) {
          classes[classKey] = {
            name: className,
            characterCount: 0,
            specs: {},
          }
        }

        if (!classes[classKey].specs[specKey]) {
          classes[classKey].specs[specKey] = {
            className,
            name: specName,
            specName,
            characterCount: 0,
            slotCounts: {},
          }
        }

        classes[classKey].characterCount += 1
        classes[classKey].specs[specKey].characterCount += 1

        this.normalizedGearItems(profile).forEach((item) => {
          const slotKey = this.aggregateSlotKey(item.slotKey)
          const itemId = item.item_id || item.id
          if (!slotKey || !itemId || !SLOT_LABELS[slotKey]) return

          const slotCounts = classes[classKey].specs[specKey].slotCounts
          if (!slotCounts[slotKey]) slotCounts[slotKey] = {}
          if (!slotCounts[slotKey][itemId]) {
            slotCounts[slotKey][itemId] = {
              itemId,
              name: item.name || `Item ${itemId}`,
              icon: item.icon || '',
              itemLevel: item.item_level || item.itemLevel || null,
              bonuses: item.bonuses || [],
              gems: item.gems || [],
              enchant: item.enchant || null,
              enchant_id: item.enchant_id || null,
              count: 0,
            }
          }
          slotCounts[slotKey][itemId].count += 1
        })
      })

      Object.values(classes).forEach((classItem) => {
        Object.values(classItem.specs).forEach((spec) => {
          spec.slots = Object.entries(spec.slotCounts).reduce((slots, [slot, items]) => {
            slots[slot] = Object.values(items)
              .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name))
              .slice(0, TOP_ITEM_LIMIT)
              .map((item) => ({
                ...item,
                share: Math.round((item.count / Math.max(spec.characterCount, 1)) * 100),
              }))
            return slots
          }, {})
          delete spec.slotCounts
        })
      })

      return classes
    },
    normalizedGearItems(profile) {
      const items = profile.gear?.items
      if (!items) return []

      if (Array.isArray(items)) {
        return items.map((item, index) => this.normalizeGearItem(item, item.slot || item.slot_key || `${index}`))
      }

      return Object.entries(items).map(([slotKey, item]) => this.normalizeGearItem(item, slotKey))
    },
    normalizeGearItem(item, slotKey) {
      return {
        ...(item || {}),
        slotKey: normalizeSlotKey(slotKey || item?.slot || item?.inventory_type?.type),
      }
    },
    bestItem(slot) {
      return this.selectedBestItems[slot] || null
    },
    itemForDisplaySlot(slot) {
      const items = this.itemsForSlot(slot)
      if (slot === 'finger2' || slot === 'trinket2') {
        return items[1] || null
      }
      return items[0] || null
    },
    itemsForSlot(slot) {
      return this.selectedSpecData?.slots?.[this.aggregateSlotKey(slot)] || []
    },
    aggregateSlotKey(slot) {
      const normalizedSlot = normalizeSlotKey(slot)
      if (normalizedSlot === 'finger1' || normalizedSlot === 'finger2') return 'finger'
      if (normalizedSlot === 'trinket1' || normalizedSlot === 'trinket2') return 'trinket'
      return normalizedSlot
    },
    itemName(slot) {
      return this.bestItem(slot)?.name || this.slotLabel(slot)
    },
    itemMeta(slot) {
      const item = this.bestItem(slot)
      if (!item && (slot === 'shirt' || slot === 'tabard')) return '-'
      return item ? `${item.count} equipped · ${item.share}%` : 'No data'
    },
    slotLabel(slot) {
      return SLOT_LABELS[slot] || 'Empty slot'
    },
    itemIconUrl(item) {
      if (!item?.icon) return ''
      const icon = String(item.icon)
      if (icon.startsWith('http')) return icon
      return `${ICON_BASE_URL}/${icon}.jpg`
    },
    wowheadItemUrl(item) {
      const itemId = item?.itemId || item?.item_id || item?.id
      return itemId ? `https://www.wowhead.com/item=${itemId}` : 'https://www.wowhead.com/'
    },
    wowheadItemData(item) {
      const itemId = item?.itemId || item?.item_id || item?.id
      if (!itemId) return null
      const params = [`item=${itemId}`]
      const itemLevel = item.itemLevel || item.item_level
      if (itemLevel) params.push(`ilvl=${itemLevel}`)
      if (Array.isArray(item.bonuses) && item.bonuses.length) params.push(`bonus=${item.bonuses.join(':')}`)
      if (Array.isArray(item.gems) && item.gems.length) params.push(`gems=${item.gems.map((gem) => gem.item_id || gem.id || gem).join(':')}`)
      if (item.enchant?.id || item.enchant_id) params.push(`ench=${item.enchant?.id || item.enchant_id}`)
      return params.join('&')
    },
    openSlot(slot) {
      if (!this.itemsForSlot(slot).length) return
      this.activeSlot = slot
      this.closingSlot = false
      document.body.classList.add('is-scroll-locked')
      this.$nextTick(() => this.refreshWowheadTooltips())
    },
    closeSlot() {
      if (!this.activeSlot || this.closingSlot) return
      this.closingSlot = true
      window.setTimeout(() => {
        this.activeSlot = null
        this.closingSlot = false
        document.body.classList.remove('is-scroll-locked')
      }, 190)
    },
    closeSlotOnEscape(event) {
      if (event.key === 'Escape' && this.activeSlot) {
        this.closeSlot()
      }
    },
    refreshWowheadTooltips() {
      if (window.$WowheadPower?.refreshLinks) {
        window.$WowheadPower.refreshLinks()
      }
    },
  },
}
</script>
